'use client';

import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { Input } from '@/components/ds';
import styles from './OwnerAddressField.module.css';

/**
 * The owner-facing address picker: 3-character minimum, 300ms debounce, a
 * dropdown of Places suggestions, and a resolve on pick.
 *
 * Sibling of `app/admin/_components/AddressAutocomplete.tsx`, deliberately NOT
 * a reuse of it. That one calls `admin.addressSuggest` because a platform
 * admin has no `bid` to satisfy the owner-facing gate; this one calls
 * `business.addressSuggest`/`addressResolve`, which are `protectedProcedure`
 * plus `requireRoleOrNoBusiness` — i.e. they work for someone who does not own
 * a business yet, which is every caller of this wizard.
 *
 * Its resolve DOES return a `city` (it did not until #458, and this comment
 * used to say so). That city is the only source `business.city` has for an
 * owner-created business, so `onSelect` reports it and every caller must
 * forward it to whatever persists the address.
 *
 * ⚠️ **Typed text is not an address.** `onChangeText` reports what was typed;
 * `onSelect` fires ONLY on a real pick and is the only thing that produces
 * coordinates. The wizard's readiness rule (`hasAddressPin`) and the submit
 * payload both key off the coordinates, never the text — a business whose
 * owner typed an address and never picked it has no address at all as far as
 * the server is concerned. That is issue #400's rule; do not "helpfully"
 * synthesise a pin from the text.
 */
export function OwnerAddressField(props: {
  label: string;
  placeholder: string;
  value: string;
  onChangeText: (text: string) => void;
  onSelect: (selection: {
    address: string;
    lat: number;
    lng: number;
    /** Places' locality for the picked address; null when it derived none. */
    city: string | null;
  }) => void;
  disabled?: boolean;
}) {
  const { label, placeholder, value, onChangeText, onSelect, disabled } = props;
  const debounced = useDebouncedValue(value.trim(), 300);
  // Opened by a KEYSTROKE only, never by `value` changing — the parent seeds
  // this field from the draft on mount, and a dropdown that springs open on a
  // restored value is the wizard shouting at someone who has not typed yet.
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();

  // Mirrors the server's own guard: under 3 characters never reaches Google,
  // so it costs nothing and burns none of the owner's Places quota.
  const enabled = open && debounced.length >= 3;
  const suggestQuery = trpc.business.addressSuggest.useQuery({ query: debounced }, { enabled });
  const suggestions = enabled ? (suggestQuery.data ?? []) : [];

  const handleSelect = async (placeId: string) => {
    setOpen(false);
    try {
      const resolved = await utils.business.addressResolve.fetch({ placeId });
      if (resolved) {
        onSelect({
          address: resolved.formattedAddress,
          lat: resolved.lat,
          lng: resolved.lng,
          city: resolved.city,
        });
      }
    } catch {
      // Quiet: keep whatever the owner already had rather than replacing the
      // field with an error. The pin simply does not get set, and the chapter
      // still reports the address as outstanding.
    }
  };

  return (
    <div className={styles.wrap}>
      <Input
        label={label}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          setOpen(true);
          onChangeText(e.target.value);
        }}
      />
      {suggestions.length > 0 && (
        <ul className={styles.dropdown} role="listbox" aria-label={label}>
          {suggestions.map((item) => (
            <li key={item.placeId}>
              <button
                type="button"
                className={styles.row}
                onClick={() => void handleSelect(item.placeId)}
              >
                {item.description}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
